import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../lib/api";
import { motion } from "framer-motion";
import { Swords, Zap, ArrowLeft } from "lucide-react";
import ModeGate from "../components/ModeGate";
import useSubjectTopics from "../hooks/useSubjectTopics";

const SUBJECTS = ["Math","Physics","Chemistry","Biology","English","History","Geography"];
const GRADES   = ["Grade 9","Grade 10","Grade 11","Grade 12"];

export default function Casual() {
  return (
    <ModeGate mode="Casual" accent="blue">
      <CasualSetup />
    </ModeGate>
  );
}

function CasualSetup() {
  const nav = useNavigate();
  const [subject, setSubject] = useState("Math");
  const [grade, setGrade]     = useState("Grade 10");
  const { topics, topic, setTopic } = useSubjectTopics(subject, grade, { extra: ["All Topics"] });
  const [poolStatus, setPoolStatus] = useState(null);

  // Re-check pool whenever filter changes so the user can see availability.
  useEffect(() => {
    const params = new URLSearchParams({ subject });
    if (topic && topic !== "All Topics") params.set("topic", topic);
    if (grade) params.set("grade", grade);
    apiClient.get(`/ai/pool-status?${params}`)
      .then(r => setPoolStatus(r.data))
      .catch(()=> setPoolStatus(null));
  }, [subject, topic, grade]);

  const startMatch = () => {
    nav("/battle", { state: { subject, topic, grade, mode: "casual" } });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <button onClick={()=>nav("/")} className="font-mono text-xs tracking-[0.3em] text-soft hover:text-white flex items-center gap-2 mb-6" data-testid="back-home"><ArrowLeft size={14}/>BACK TO MENU</button>
      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}>
        <div className="font-mono text-[10px] tracking-[0.4em] text-blue uppercase mb-2">CASUAL MODE</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl mb-8">QUEUE FOR <span className="text-blue">RANKED 1V1</span></h1>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Selector label="SUBJECT" value={subject} options={SUBJECTS} onChange={setSubject} testid="select-subject"/>
        <Selector label="TOPIC"   value={topic}   options={topics}   onChange={setTopic}   testid="select-topic"/>
        <Selector label="GRADE"   value={grade}   options={GRADES}   onChange={setGrade}   testid="select-grade"/>
      </div>

      <div className="mt-10 sm-card p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div>
          <div className="font-mono text-xs tracking-[0.3em] text-soft">MATCH SETUP</div>
          <div className="font-heading font-bold text-2xl mt-2">{subject} · {topic} · {grade}</div>
          <div className="text-soft text-sm mt-1">10 rounds · dynamic timer · ELO ranked</div>
          {poolStatus && (
            <div className="mt-3 font-mono text-[10px] tracking-widest text-faint">
              POOL: <span className="text-blue">{poolStatus.total}</span> questions · {poolStatus.unused} fresh
            </div>
          )}
        </div>
        <button className="sm-btn-primary px-8 py-4 flex items-center gap-2" onClick={startMatch} data-testid="casual-play-btn">
          <Zap size={16}/> ENTER QUEUE
        </button>
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-soft">
        <Info icon={<Swords className="text-blue" size={18}/>} title="FIRST CORRECT WINS" body="First player to answer correctly takes the round point."/>
        <Info icon={<Zap className="text-purple" size={18}/>} title="120s PER QUESTION" body="You get 120 seconds on every question. First correct answer wins the round."/>
        <Info icon={<Swords className="text-blue" size={18}/>} title="STRICT FILTERS"  body="Questions match your subject & grade only — no leakage."/>
      </div>
    </div>
  );
}

function Selector({ label, value, options, onChange, testid }) {
  return (
    <label className="sm-card p-5 block">
      <div className="font-mono text-[10px] tracking-[0.3em] text-blue">{label}</div>
      <select className="sm-input mt-3" value={value} onChange={e=>onChange(e.target.value)} data-testid={testid}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Info({ icon, title, body }) {
  return (
    <div className="sm-card p-4">
      <div className="flex items-center gap-2">{icon}<div className="font-mono text-[11px] tracking-[0.25em]">{title}</div></div>
      <div className="text-soft mt-2 text-sm">{body}</div>
    </div>
  );
}
